import { useFormikContext } from "formik";

import {
  FeatureFlag,
  useFeatureFlag,
} from "@vega-api/extended/auth/useFeatureFlag";
import { MoneyInput } from "@vega-core/components/Form/Controls/Input/MoneyInput/MoneyInput";
import { CheckboxField } from "@vega-core/components/Form/Fields/CheckboxField";
import { DateField } from "@vega-core/components/Form/Fields/DateField";
import { MoneyField } from "@vega-core/components/Form/Fields/MoneyField";
import * as m from "@vega-core/paraglide/messages.js";
import { FormGroup } from "@vega-modules/scenes/CoInvestmentsGP/components/CoInvestmentEditForm/components/FormGroup";

import { type CoInvestmentEditFormData } from "../CoInvestmentEditForm";

type DealInformationSectionProps = {
  editable?: boolean;
};

export const DealInformationSection = ({
  editable,
}: DealInformationSectionProps) => {
  const { values } = useFormikContext<CoInvestmentEditFormData>();
  const dealCurrency = values.currency;
  const { featureFlag } = useFeatureFlag();
  const isBookBuildEnabled =
    featureFlag[FeatureFlag.COINVEST_BOOK_BUILD_VD_26153];
  const isReportingEnabled =
    featureFlag[FeatureFlag.COINVEST_REPORTING_VD_26152];

  const totalDealSizeAmount = values.totalDealSize?.amount;
  const fundContributionAmount = values.fundContributionAmount?.amount;
  const derivedTargetAmount =
    isBookBuildEnabled && totalDealSizeAmount !== undefined
      ? Math.max(totalDealSizeAmount - (fundContributionAmount ?? 0), 0)
      : undefined;

  return (
    <FormGroup dataTestId="deal-information" label={m.common_dealInformation()}>
      <div className="grid grid-cols-1 gap-4">
        <div className="flex flex-col gap-2">
          <DateField
            disabled={!editable}
            label={m.common_closingDate()}
            labelTag={m.coInvestments_editDealForm_internalUseOnly()}
            name="closingDate"
            onlyFuture={isReportingEnabled}
          />
          {isReportingEnabled && values.closingDate ? (
            <CheckboxField
              disabled={!editable}
              label={m.coInvestments_autoClose()}
              name="autoClose"
            />
          ) : null}
        </div>

        <MoneyField
          allowAbbreviations
          currency={dealCurrency}
          disabled={!editable}
          label={m.coInvestments_totalDealSize()}
          labelTag={m.coInvestments_editDealForm_internalUseOnly()}
          name="totalDealSize"
        />

        {isBookBuildEnabled ? (
          <MoneyField
            allowAbbreviations
            currency={dealCurrency}
            disabled={!editable}
            label={m.coInvestments_gpFundContribution()}
            labelTag={m.coInvestments_editDealForm_internalUseOnly()}
            name="fundContributionAmount"
          />
        ) : null}

        {isBookBuildEnabled ? (
          <MoneyInput
            allowAbbreviations
            currency={dealCurrency}
            disabled
            isOptional={false}
            label={m.common_targetRaise()}
            labelTag={m.coInvestments_editDealForm_internalUseOnly()}
            name="targetAmount.amount"
            value={derivedTargetAmount ?? ""}
          />
        ) : (
          <MoneyField
            allowAbbreviations
            currency={dealCurrency}
            disabled={!editable}
            isRequired
            label={m.common_targetRaise()}
            labelTag={m.coInvestments_editDealForm_internalUseOnly()}
            name="targetAmount"
          />
        )}
      </div>
    </FormGroup>
  );
};
